import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Plus,
  Save,
  RotateCcw,
  User,
  MapPin,
  Phone
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import paymentTermsMasterService from '../../services/paymentTermsMasterService'
import customerMasterService from '../../services/customerMasterService'
import { getStateOptions } from '../../constants/indianStates'
import '../Accounts/PaymentForm.css'

const CustomerMasterEdit = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { id: urlId } = useParams() // Get ID from URL
  const customerData = location.state?.customerData || {}

  const [activeTab, setActiveTab] = useState('customerInfo')
  const [paymentTermsOptions, setPaymentTermsOptions] = useState([])
  const [customerId, setCustomerId] = useState(() => {
    return urlId || customerData._id || customerData.id
  })
  const [loading, setLoading] = useState(false)
  const [fullCustomerData, setFullCustomerData] = useState(null)
  const [formData, setFormData] = useState({
    // Customer Info - Matching backend model
    customerCode: '',
    customerName: '', // Backend: customerName
    customerNickName: '',
    customerCategory: '',
    customerType: '',
    region: '', // Required field
    customerUdyogAadhar: '',
    customerPAN: '', // Backend: customerPAN
    GSTIN: '', // Backend: GSTIN
    GSTClassification: '', // Backend: GSTClassification
    creditLimit: '',
    customerPaymentTerms: '', // Backend: customerPaymentTerms
    customerCurrency: 'INR', // Backend: customerCurrency
    frightCharge: '',
    transporter: '',
    destination: '',
    customerWebsite: '',
    eximCode: '',
    transporterGSTIN: '',
    transporterId: '',
    categoryType: 'Domestic',
    isCustomerActive: 'A', // Backend: isCustomerActive
    // Legacy fields for form compatibility
    customerNameOnTaxInvoice: '',
    panCardNo: '',
    paymentTerms: '',
    gstin: '',
    gstClassification: '',
    currency: '',
    status: 'Active'
  })

  const [addresses, setAddresses] = useState([
    {
      id: 1,
      country: '',
      stateProvince: '',
      cityDistrict: '',
      pinCode: '',
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      addressLine4: ''
    }
  ])

  const [contacts, setContacts] = useState([
    {
      id: 1,
      contactPerson: '',
      email: '',
      mobile: '',
      alternatePhone: '',
      department: '',
      designation: ''
    }
  ])

  // Load customer data and pre-fill form
  useEffect(() => {
    const loadCustomerData = async () => {
      try {
        // CRITICAL: If no URL ID, redirect immediately
        if (!urlId) {
          toast.error('Invalid customer ID. Redirecting...')
          navigate('/sales/customer-master')
          return
        }
        
        // Priority: URL ID > location.state ID > fetch by code
        let id = urlId || customerData._id || customerData.id
        let dataToUse = customerData
        
        // If we have URL ID but no customerData, fetch the customer
        if (urlId && (!customerData || Object.keys(customerData).length === 0)) {
          setLoading(true)
          try {
            const response = await customerMasterService.getCustomerById(urlId)
            if (response.success && response.data) {
              dataToUse = response.data
            } else {
              toast.error('Customer not found')
              navigate('/sales/customer-master')
              return
            }
          } catch (fetchError) {
            console.error('Error fetching customer by ID:', fetchError)
            toast.error('Failed to load customer data')
            navigate('/sales/customer-master')
            return
          } finally {
            setLoading(false)
          }
        }

        // If no ID but we have customerCode, try to fetch the customer
        if (!id && customerData.customerCode) {
          setLoading(true)
          
          // Try to get customer by code - search for it
          try {
            const response = await customerMasterService.getAllCustomers({ 
              search: customerData.customerCode,
              limit: 1 
            })
            
            if (response.success && response.data && response.data.length > 0) {
              const foundCustomer = response.data.find(c => c.customerCode === customerData.customerCode)
              if (foundCustomer) {
                id = foundCustomer._id || foundCustomer.id
                dataToUse = foundCustomer
              }
            }
          } catch (fetchError) {
            console.error('Error fetching customer by code:', fetchError)
          } finally {
            setLoading(false)
          }
        }

        // If still no ID, show error and redirect
        if (!id) {
          toast.error('Customer ID is missing. Please navigate back and try again.')
          setTimeout(() => {
            navigate('/sales/customer-master')
          }, 2000)
          return
        }

        // Store the ID and full data - URL ID takes priority
        const finalId = urlId || id
        setCustomerId(finalId)
        setFullCustomerData(dataToUse)
        
        // Verify ID was set
        if (!finalId) {
          toast.error('Customer ID is missing. Redirecting...')
          setTimeout(() => {
            navigate('/sales/customer-master')
          }, 2000)
          return
        }
        
        console.log('21. ✅ Customer ID successfully set:', finalId)
        console.log('=== useEffect: loadCustomerData Completed ===')
        
        // Use the fetched/updated data
        const finalData = dataToUse

        setFormData({
          // Map backend fields to form fields
          customerCode: finalData.customerCode || '',
          customerName: finalData.customerName || '',
          customerNickName: finalData.customerNickName || '',
          customerCategory: finalData.customerCategory || '',
          customerType: finalData.customerType || '',
          region: finalData.region || '',
          customerUdyogAadhar: finalData.customerUdyogAadhar || '',
          customerPAN: finalData.customerPAN || '',
          GSTIN: finalData.GSTIN || '',
          GSTClassification: finalData.GSTClassification || '',
          creditLimit: finalData.creditLimit || '',
          customerPaymentTerms: finalData.customerPaymentTerms || '',
          customerCurrency: finalData.customerCurrency || 'INR',
          frightCharge: finalData.frightCharge || '',
          transporter: finalData.transporter || '',
          destination: finalData.destination || '',
          customerWebsite: finalData.customerWebsite || '',
          eximCode: finalData.eximCode || '',
          transporterGSTIN: finalData.transporterGSTIN || '',
          transporterId: finalData.transporterId || '',
          categoryType: finalData.categoryType || 'Domestic',
          isCustomerActive: finalData.isCustomerActive || 'A',
          // Legacy mapping for backward compatibility
          customerNameOnTaxInvoice: finalData.customerName || '',
          panCardNo: finalData.customerPAN || '',
          paymentTerms: finalData.customerPaymentTerms || '',
          gstin: finalData.GSTIN || '',
          gstClassification: finalData.GSTClassification || '',
          currency: finalData.customerCurrency || 'INR',
          status: finalData.isCustomerActive === 'A' ? 'Active' : 'Inactive'
        })
        
        // Map addresses from backend data
        if (finalData.customerBillingAddress && finalData.customerBillingAddress.length > 0) {
          setAddresses(finalData.customerBillingAddress.map((addr, index) => ({
            id: index + 1,
            country: addr.country || '',
            stateProvince: addr.state || '',
            cityDistrict: addr.city || '',
            pinCode: addr.pinCode || '',
            addressLine1: addr.line1 || '',
            addressLine2: addr.line2 || '',
            addressLine3: addr.line3 || '',
            addressLine4: addr.line4 || ''
          })))
        }

        // Map contacts from backend data
        if (finalData.customerContactInfo && finalData.customerContactInfo.length > 0) {
          setContacts(finalData.customerContactInfo.map((contact, index) => ({
            id: index + 1,
            contactPerson: contact.contactPersonName || '',
            email: contact.contactPersonEmail || '',
            mobile: contact.contactPersonNumber || '',
            alternatePhone: '', // Not in backend model
            department: contact.contactPersonDepartment || '',
            designation: contact.contactPersonDesignation || ''
          })))
        }
      } catch (error) {
        console.error('Error loading customer data:', error)
        toast.error('Failed to load customer data')
        setTimeout(() => {
          navigate('/sales/customer-master')
        }, 2000)
      }
    }

    loadCustomerData()
    
    // Fetch payment terms
    fetchPaymentTerms()
  }, [urlId, customerData, navigate])

  const fetchPaymentTerms = async () => {
    try {
      const response = await paymentTermsMasterService.getActivePaymentTerms()
      if (response && response.success) {
        setPaymentTermsOptions(response.data || [])
      }
    } catch (error) {
      console.error('Error fetching payment terms:', error)
      toast.error('Failed to load payment terms')
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleAddressChange = (addressId, field, value) => {
    setAddresses(prev => 
      prev.map(addr => 
        addr.id === addressId 
          ? { ...addr, [field]: value }
          : addr
      )
    )
  }

  const addAddress = () => {
    const newAddress = {
      id: Date.now(),
      country: '',
      stateProvince: '',
      cityDistrict: '',
      pinCode: '',
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      addressLine4: ''
    }
    setAddresses(prev => [...prev, newAddress])
  }

  const removeAddress = (addressId) => {
    if (addresses.length > 1) {
      setAddresses(prev => prev.filter(addr => addr.id !== addressId))
    }
  }

  const handleContactChange = (contactId, field, value) => {
    setContacts(prev => 
      prev.map(contact => 
        contact.id === contactId 
          ? { ...contact, [field]: value }
          : contact
      )
    )
  }

  const addContact = () => {
    const newContact = {
      id: Date.now(),
      contactPerson: '',
      email: '',
      mobile: '',
      alternatePhone: '',
      department: '',
      designation: ''
    }
    setContacts(prev => [...prev, newContact])
  }

  const removeContact = (contactId) => {
    if (contacts.length > 1) {
      setContacts(prev => prev.filter(contact => contact.id !== contactId))
    }
  }

  const handleSave = async () => {
    try {
      // CRITICAL: URL ID is the primary source - it should always be present
      if (!urlId) {
        toast.error('Invalid customer ID. Please navigate back and try again.')
        navigate('/sales/customer-master')
        return
      }
      
      // Use URL ID as primary source
      let idToUpdate = urlId
      
      // Final fallback: try to get from customerData
      if (!idToUpdate) {
        idToUpdate = customerData._id || customerData.id || fullCustomerData?._id || fullCustomerData?.id
      }
      
      // Last resort: fetch by customerCode
      if (!idToUpdate && formData.customerCode) {
        toast.info('Looking up customer ID...')
        
        try {
          const response = await customerMasterService.getAllCustomers({ 
            search: formData.customerCode,
            limit: 10 
          })
          
          if (response.success && response.data && response.data.length > 0) {
            const foundCustomer = response.data.find(c => c.customerCode === formData.customerCode)
            if (foundCustomer) {
              idToUpdate = foundCustomer._id || foundCustomer.id
              setCustomerId(idToUpdate)
            }
          }
        } catch (fetchError) {
          console.error('Error fetching customer by code:', fetchError)
        }
      }
      
      // Final validation - this should never happen if URL has ID
      if (!idToUpdate) {
        toast.error('Customer ID is missing. Cannot update customer.')
        return
      }

      // Validate ID is a non-empty string
      if (typeof idToUpdate !== 'string' || idToUpdate.trim() === '' || idToUpdate === 'undefined' || idToUpdate === 'null') {
        toast.error('Invalid customer ID. Please navigate back and try again.')
        return
      }

      // Prepare customer data - matching backend model exactly
      const updateData = {
        customerCode: formData.customerCode,
        customerName: formData.customerName,
        customerNickName: formData.customerNickName,
        customerCategory: formData.customerCategory,
        customerType: formData.customerType,
        region: formData.region,
        customerUdyogAadhar: formData.customerUdyogAadhar,
        customerPAN: formData.customerPAN,
        GSTIN: formData.GSTIN,
        GSTClassification: formData.GSTClassification,
        customerBillingAddress: addresses.map(addr => ({
          line1: addr.addressLine1,
          line2: addr.addressLine2,
          line3: addr.addressLine3,
          line4: addr.addressLine4,
          state: addr.stateProvince,
          city: addr.cityDistrict,
          pinCode: addr.pinCode,
          country: addr.country,
          contactPersonName: contacts[0]?.contactPerson || '',
          contactPersonNumber: contacts[0]?.mobile || ''
        })),
        customerShippingAddress: addresses.map(addr => ({
          line1: addr.addressLine1,
          line2: addr.addressLine2,
          line3: addr.addressLine3,
          state: addr.stateProvince,
          city: addr.cityDistrict,
          pinCode: addr.pinCode,
          country: addr.country,
          contactPersonName: contacts[0]?.contactPerson || '',
          contactPersonNumber: contacts[0]?.mobile || ''
        })),
        customerContactInfo: contacts.map(contact => ({
          contactPersonName: contact.contactPerson,
          contactPersonDesignation: contact.designation,
          contactPersonDepartment: contact.department,
          contactPersonNumber: contact.mobile,
          contactPersonEmail: contact.email
        })),
        frightCharge: formData.frightCharge,
        transporter: formData.transporter,
        destination: formData.destination,
        customerCurrency: formData.customerCurrency,
        creditLimit: formData.creditLimit,
        customerPaymentTerms: formData.customerPaymentTerms,
        isCustomerActive: formData.isCustomerActive,
        customerWebsite: formData.customerWebsite,
        customerBankDetails: [], // Empty array for now
        eximCode: formData.eximCode,
        transporterGSTIN: formData.transporterGSTIN,
        transporterId: formData.transporterId,
        categoryType: formData.categoryType
      }

      // Final validation before API call - prevent calling with undefined
      if (!idToUpdate || idToUpdate === 'undefined' || idToUpdate === 'null') {
        toast.error('Customer ID is invalid. Cannot update customer.')
        return
      }

      // Update customer
      const response = await customerMasterService.updateCustomer(idToUpdate, updateData)
      
      if (response && response.success) {
        toast.success('Customer updated successfully!')
        navigate('/sales/customer-master')
      } else {
        toast.error(response?.message || 'Failed to update customer')
      }
    } catch (error) {
      console.error('Error updating customer:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
        navigate('/login')
      } else if (error.response?.status === 404) {
        toast.error('Customer not found')
      } else {
        toast.error('Failed to update customer')
      }
    }
  }

  const handleReset = () => {
    setFormData({
      customerCategory: customerData.customerCategory || '',
      gstClassification: customerData.gstClassification || '',
      creditLimit: customerData.creditLimit || '',
      customerCode: customerData.customerCode || '',
      gstin: customerData.gstin || '',
      paymentTerms: customerData.paymentTerms || '',
      customerNameOnTaxInvoice: customerData.customerNameOnTaxInvoice || '',
      panCardNo: customerData.panCardNo || '',
      freightTerms: customerData.freightTerms || '',
      customerVendorCode: customerData.customerVendorCode || '',
      currency: customerData.currency || '',
      status: customerData.status || 'Active',
      contactPerson: customerData.contactPerson || '',
      email: customerData.email || '',
      phone: customerData.phone || '',
      mobile: customerData.mobile || '',
      website: customerData.website || '',
      alternateEmail: customerData.alternateEmail || '',
      alternatePhone: customerData.alternatePhone || ''
    })
  }

  const handleBack = () => {
    navigate('/sales/customer-master')
  }

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Sales',
      path: '/sales',
      onClick: () => navigate('/sales')
    },
    {
      label: 'Masters',
      path: '/sales',
      onClick: () => navigate('/sales')
    },
    {
      label: 'Customer Master',
      path: '/sales/customer-master',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Customer Master - Edit</h1>
          <p>Edit customer information</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container pay-entry">
        <div className="sac-form-header">
          <h2>Customer Master Entry</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'customerInfo' ? 'active' : ''}`}
            onClick={() => setActiveTab('customerInfo')}
          >
            <User size={18} />
            <span>Customer Info</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'customerAddress' ? 'active' : ''}`}
            onClick={() => setActiveTab('customerAddress')}
          >
            <MapPin size={18} />
            <span>Customer Address</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'contactDetails' ? 'active' : ''}`}
            onClick={() => setActiveTab('contactDetails')}
          >
            <Phone size={18} />
            <span>Contact Details</span>
          </button>
        </div>
          
        <form className="form-content">
          {/* Customer Info Tab */}
          {activeTab === 'customerInfo' && (
            <div className="tab-content">
              <section className="pay-block">
                <div className="pay-block-head">
                  <div>
                    <h3>Customer</h3>
                    <p>Who this customer is, and where they sit in the business.</p>
                  </div>
                </div>
              <div className="pay-grid pay-grid-4">
                <div className="form-group">
                  <label htmlFor="customerCategory" className="form-label required">
                    Customer Category
                  </label>
                  <select
                    id="customerCategory"
                    name="customerCategory"
                    value={formData.customerCategory}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">Select Type</option>
                    <option value="Domestic">Domestic</option>
                    <option value="International">International</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="customerCode" className="form-label required">
                    Customer Code
                  </label>
                  <input
                    type="text"
                    id="customerCode"
                    name="customerCode"
                    value={formData.customerCode}
                    readOnly
                    className="form-input pay-readonly"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customerName" className="form-label required">
                    Customer Name
                  </label>
                  <input
                    type="text"
                    id="customerName"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter customer name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="region" className="form-label required">
                    Region
                  </label>
                  <select
                    id="region"
                    name="region"
                    value={formData.region}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">Select Region</option>
                    <option value="North">North</option>
                    <option value="South">South</option>
                    <option value="East">East</option>
                    <option value="West">West</option>
                    <option value="Central">Central</option>
                  </select>
                </div>
              </div>
              </section>

              <section className="pay-block">
                <div className="pay-block-head">
                  <div>
                    <h3>Tax</h3>
                    <p>GST and PAN, and the currency this customer is billed in.</p>
                  </div>
                </div>
              <div className="pay-grid pay-grid-4">
                <div className="form-group">
                  <label htmlFor="GSTClassification" className="form-label required">
                    GST Classification
                  </label>
                  <select
                    id="GSTClassification"
                    name="GSTClassification"
                    value={formData.GSTClassification}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">GST Classification</option>
                    <option value="Regular">Regular</option>
                    <option value="Composition">Composition</option>
                    <option value="Unregistered">Unregistered</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="GSTIN" className="form-label required">
                    GSTIN
                  </label>
                  <input
                    type="text"
                    id="GSTIN"
                    name="GSTIN"
                    value={formData.GSTIN}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter GSTIN"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customerPAN" className="form-label required">
                    PAN Card No.
                  </label>
                  <input
                    type="text"
                    id="customerPAN"
                    name="customerPAN"
                    value={formData.customerPAN}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter PAN number"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customerCurrency" className="form-label required">
                    Currency
                  </label>
                  <select
                    id="customerCurrency"
                    name="customerCurrency"
                    value={formData.customerCurrency}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">Select</option>
                    <option value="INR">INR - Indian Rupee</option>
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                  </select>
                </div>
              </div>
              </section>

              <section className="pay-block">
                <div className="pay-block-head">
                  <div>
                    <h3>Terms</h3>
                    <p>Credit, payment terms, freight, and whether the customer is active.</p>
                  </div>
                </div>
              <div className="pay-grid pay-grid-4">
                <div className="form-group">
                  <label htmlFor="creditLimit" className="form-label">
                    Credit Limit
                  </label>
                  <input
                    type="number"
                    id="creditLimit"
                    name="creditLimit"
                    value={formData.creditLimit}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customerPaymentTerms" className="form-label required">
                    Payment Terms
                  </label>
                  <select
                    id="customerPaymentTerms"
                    name="customerPaymentTerms"
                    value={formData.customerPaymentTerms}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">Select Payment Terms</option>
                    {paymentTermsOptions.map((term) => (
                      <option key={term._id} value={term.description}>
                        {term.description}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="freightTerms" className="form-label">
                    Freight Terms
                  </label>
                  <select
                    id="freightTerms"
                    name="freightTerms"
                    value={formData.freightTerms}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">Select Freight Terms</option>
                    <option value="FOB">FOB</option>
                    <option value="CIF">CIF</option>
                    <option value="EXW">EXW</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="status" className="form-label required">
                    Status
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>
              </section>
            </div>
          )}

          {/* Customer Address Tab */}
          {activeTab === 'customerAddress' && (
            <div className="tab-content">
              {addresses.map((address, index) => (
                <section key={address.id} className="pay-block">
                  <div className="pay-block-head">
                    <div>
                      <h3>{index === 0 ? 'Primary address' : `Address ${index + 1}`}</h3>
                      <p>{index === 0 ? 'The main billing address. Add another if this customer has more than one.' : 'An additional address for this customer.'}</p>
                    </div>
                    {index === 0 && (
                      <button 
                        type="button" 
                        className="add-address-button"
                        onClick={addAddress}
                      >
                        <Plus size={16} />
                      </button>
                    )}
                    {index > 0 && (
                      <button 
                        type="button" 
                        className="remove-address-button"
                        onClick={() => removeAddress(address.id)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="pay-grid pay-grid-4">
                    <div className="form-group">
                      <label className="form-label required">Country</label>
                      <select
                        value={address.country}
                        onChange={(e) => handleAddressChange(address.id, 'country', e.target.value)}
                        className="form-input"
                      >
                        <option value="">Select Country</option>
                        <option value="India">India</option>
                        <option value="USA">USA</option>
                        <option value="UK">UK</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label required">State/Province</label>
                      <select
                        value={address.stateProvince}
                        onChange={(e) => handleAddressChange(address.id, 'stateProvince', e.target.value)}
                        className="form-input"
                      >
                        <option value="">Select State</option>
                        {getStateOptions().map(state => (
                          <option key={state.value} value={state.value}>
                            {state.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label required">City/District</label>
                      <input
                        type="text"
                        value={address.cityDistrict}
                        onChange={(e) => handleAddressChange(address.id, 'cityDistrict', e.target.value)}
                        className="form-input"
                        placeholder="Enter city"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Pin Code</label>
                      <input
                        type="text"
                        value={address.pinCode}
                        onChange={(e) => handleAddressChange(address.id, 'pinCode', e.target.value)}
                        className="form-input"
                        placeholder="Enter pin code"
                      />
                    </div>
                  </div>
                  <div className="pay-grid pay-grid-4 pay-follow">
                    <div className="form-group">
                      <label className="form-label required">Address Line 1</label>
                      <input
                        type="text"
                        value={address.addressLine1}
                        onChange={(e) => handleAddressChange(address.id, 'addressLine1', e.target.value)}
                        className="form-input"
                        placeholder="Enter address line 1"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Address Line 2</label>
                      <input
                        type="text"
                        value={address.addressLine2}
                        onChange={(e) => handleAddressChange(address.id, 'addressLine2', e.target.value)}
                        className="form-input"
                        placeholder="Enter address line 2"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Address Line 3</label>
                      <input
                        type="text"
                        value={address.addressLine3}
                        onChange={(e) => handleAddressChange(address.id, 'addressLine3', e.target.value)}
                        className="form-input"
                        placeholder="Enter address line 3"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Address Line 4</label>
                      <input
                        type="text"
                        value={address.addressLine4}
                        onChange={(e) => handleAddressChange(address.id, 'addressLine4', e.target.value)}
                        className="form-input"
                        placeholder="Enter address line 4"
                      />
                    </div>
                  </div>
                </section>
              ))}
            </div>
          )}

          {/* Contact Details Tab */}
          {activeTab === 'contactDetails' && (
            <div className="tab-content">
              {contacts.map((contact, index) => (
                <section key={contact.id} className="pay-block">
                  <div className="pay-block-head">
                    <div>
                      <h3>{index === 0 ? 'Primary contact' : `Contact ${index + 1}`}</h3>
                      <p>{index === 0 ? 'The person we write to first.' : 'Another person at this customer.'}</p>
                    </div>
                    {index === 0 && (
                      <button 
                        type="button" 
                        className="add-contact-button"
                        onClick={addContact}
                      >
                        <Plus size={16} />
                      </button>
                    )}
                    {index > 0 && (
                      <button 
                        type="button" 
                        className="remove-contact-button"
                        onClick={() => removeContact(contact.id)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="pay-grid pay-grid-4">
                    <div className="form-group">
                      <label className="form-label required">Contact Person</label>
                      <input
                        type="text"
                        value={contact.contactPerson}
                        onChange={(e) => handleContactChange(contact.id, 'contactPerson', e.target.value)}
                        className="form-input"
                        placeholder="Enter contact person name"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Mobile</label>
                      <input
                        type="tel"
                        value={contact.mobile}
                        onChange={(e) => handleContactChange(contact.id, 'mobile', e.target.value)}
                        className="form-input"
                        placeholder="Enter mobile number"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Alternate Phone</label>
                      <input
                        type="tel"
                        value={contact.alternatePhone}
                        onChange={(e) => handleContactChange(contact.id, 'alternatePhone', e.target.value)}
                        className="form-input"
                        placeholder="Enter alternate phone"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Email</label>
                      <input
                        type="email"
                        value={contact.email}
                        onChange={(e) => handleContactChange(contact.id, 'email', e.target.value)}
                        className="form-input"
                        placeholder="Enter email address"
                      />
                    </div>
                  </div>
                  <div className="pay-grid pay-grid-2 pay-follow">
                    <div className="form-group">
                      <label className="form-label">Department</label>
                      <input
                        type="text"
                        value={contact.department}
                        onChange={(e) => handleContactChange(contact.id, 'department', e.target.value)}
                        className="form-input"
                        placeholder="Enter department"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Designation</label>
                      <input
                        type="text"
                        value={contact.designation}
                        onChange={(e) => handleContactChange(contact.id, 'designation', e.target.value)}
                        className="form-input"
                        placeholder="Enter designation"
                      />
                    </div>
                  </div>
                </section>
              ))}
            </div>
          )}

          {/* Action Buttons - Available in all tabs */}
          <div className="form-actions">
            <button
              type="button"
              onClick={handleReset}
              className="btn-secondary"
            >
              <RotateCcw size={16} />
              Reset
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="btn-primary"
            >
              <Save size={16} />
              Update
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CustomerMasterEdit